import { useFormik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SESSION_EXP_STORAGE_KEY } from '../constants/auth';
import { loginUser } from '../store/actions/general';
import { isValidEmail, statusIsGood, statusIsUnauthorized } from '../utils';
import { Button } from './general/Button';
import { useSession } from '../contexts/SessionContext';
import { LogIn, KeyRound, Lock } from 'lucide-react';

let autoLoginCount = 0;

export const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const redirectFrom = location.state?.from || '/';

  const { sessionId } = useSession();

  const [submitting, setSubmitting] = useState(false);

  const logInUser = useCallback(
    async ({ email, password }) => {
      setSubmitting(true);

      try {
        const rawResp = await fetch('/api/auth/login', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: email,
            password: password,
            sessionId: sessionId,
          }),
        });
        if (statusIsGood(rawResp.status)) {
          const resp = await rawResp.json();
          dispatch(
            loginUser({
              email: resp.user.email,
              firstName: resp.user.first_name,
              lastName: resp.user.last_name,
              role: resp.user.role,
              isActive: resp.user.active,
            }),
          );
          const sessionExpDate = new Date(resp.expirationTime);
          const sessionExpTimeMs = sessionExpDate.getTime();
          localStorage.setItem(SESSION_EXP_STORAGE_KEY, sessionExpTimeMs);
          setSubmitting(false);
          // Navigate to the page intended after login. This allows for
          // deep linking
          navigate(redirectFrom);
          return;
        } else if (statusIsUnauthorized(rawResp.status)) {
          throw new Error('Unable to log in with the provided credentials. Please try again.');
        }
        throw new Error('Something went wrong. Please try again later.');
      } catch (error) {
        toast.error(error.message);
        setSubmitting(false);
      }
    },
    // Don't need to worry about location because it won't change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, navigate],
  );

  useEffect(() => {
    // In development allow auto login
    // To expedite logging in as multiple users, we can auto login either
    // 1) by passing query params to localhost:3000/login
    //    ex: localhost:3000/login?email=someemail@januaryadvisors.com&password=fakepassword
    // 2) by setting an auto login in the .env variables
    // If query params are passed, these are saved in local storage making hot reloading possible,
    // so you should only have to pass the query params once per session
    if (process.env.NODE_ENV === 'development' && autoLoginCount === 0) {
      autoLoginCount++;

      const AUTO_LOGIN = true; // Set to false to prevent auto login

      if (!AUTO_LOGIN) {
        return;
      }

      const queryString = window.location.search;

      const storageEmail = sessionStorage.getItem('email');
      const storagePassword = sessionStorage.getItem('password');
      if (queryString) {
        const urlParams = new URLSearchParams(queryString);
        const email = urlParams.get('email');
        const password = urlParams.get('password');
        if (email && password) {
          logInUser({
            email,
            password,
          });
          sessionStorage.setItem('email', email);
          sessionStorage.setItem('password', password);
        }
      } else if (storageEmail && storagePassword) {
        logInUser({
          email: storageEmail,
          password: storagePassword,
        });
      } else if (
        process.env.REACT_APP_AUTO_LOGIN_EMAIL &&
        process.env.REACT_APP_AUTO_LOGIN_PASSWORD
      ) {
        logInUser({
          email: process.env.REACT_APP_AUTO_LOGIN_EMAIL,
          password: process.env.REACT_APP_AUTO_LOGIN_PASSWORD,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validate: values => {
      const errors = {};
      if (!values.email) {
        errors.email = 'Required';
      } else if (!isValidEmail(values.email)) {
        errors.email = 'Please enter a valid email';
      }
      if (!values.password) {
        errors.password = 'Required';
      }
      return errors;
    },
    onSubmit: logInUser,
  });

  return (
    <main className="flex flex-col min-h-screen">
      <div className="bg-gradient-to-br from-cyan-50 to-white pt-12">
        <div className="flex flex-col items-center mb-12">
          <LogIn className="w-16 h-16 text-cyan-500 mb-4" />
          <h2 className="text-4xl md:text-5xl font-light text-black mb-4">Welcome Back</h2>
          <p className="text-xl text-gray-600 font-light">Sign in to your account to continue</p>
        </div>

        <div className="container mx-auto px-6 pb-12">
          <div className="max-w-md mx-auto">
            <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-lg">
              <form onSubmit={formik.handleSubmit} className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className="text-gray-700 font-medium">
                    Email
                  </label>
                  <input
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:outline-none transition-colors"
                    id="email"
                    type="email"
                    onChange={formik.handleChange}
                    value={formik.values.email}
                  />
                  {formik.touched.email && formik.errors.email ? (
                    <p className="text-red-500 text-sm">{formik.errors.email}</p>
                  ) : null}
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="password" className="text-gray-700 font-medium">
                    Password
                  </label>
                  <input
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:outline-none transition-colors"
                    id="password"
                    type="password"
                    onChange={formik.handleChange}
                    value={formik.values.password}
                  />
                  {formik.touched.password && formik.errors.password ? (
                    <p className="text-red-500 text-sm">{formik.errors.password}</p>
                  ) : null}
                </div>

                <div className="flex justify-center">
                  <Button isLoading={submitting} type="submit">
                    Log In
                  </Button>
                </div>
              </form>

              <NavLink
                to="/login/forgot-password"
                state={{ from: redirectFrom }}
                className="flex justify-center mt-6 text-cyan-500 hover:text-cyan-600 underline transition-colors"
              >
                Forgot password?
              </NavLink>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export const ForgotPassword = () => {
  const location = useLocation();

  const redirectFrom = location.state?.from || '/';

  const [submitting, setSubmitting] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validate: values => {
      const errors = {};
      if (!values.email) {
        errors.email = 'Required';
      } else if (!isValidEmail(values.email)) {
        errors.email = 'Please enter a valid email';
      }
      return errors;
    },
    onSubmit: async values => {
      setSubmitting(true);

      try {
        const rawResp = await fetch('/api/auth/forgot-password', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: values.email,
          }),
        });
        if (statusIsGood(rawResp.status)) {
          // pass the redirect path to the reset page
          localStorage.setItem('passwordResetRedirect', redirectFrom);
          setResetEmailSent(values.email);
          setSubmitting(false);
          return;
        } else {
          throw new Error('Something went wrong. Please try again later.');
        }
      } catch (error) {
        toast.error(error.message);
        setSubmitting(false);
      }
    },
  });

  if (resetEmailSent) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="bg-gradient-to-br from-cyan-50 to-white pt-12">
          <div className="flex flex-col items-center mb-12">
            <h2 className="text-4xl md:text-5xl font-light text-black mb-4">Check Your Email</h2>
          </div>

          <div className="container mx-auto px-6 pb-12">
            <div className="max-w-md mx-auto">
              <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-lg text-center">
                <p className="text-gray-700">
                  Password reset instructions have been sent to{' '}
                  <span className="font-semibold text-cyan-600">{resetEmailSent}</span>. Please
                  check your inbox.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main className="flex flex-col min-h-screen">
      <div className="bg-gradient-to-br from-cyan-50 to-white pt-12">
        <div className="flex flex-col items-center mb-12">
          <KeyRound className="w-16 h-16 text-cyan-500 mb-4" />
          <h2 className="text-4xl md:text-5xl font-light text-black mb-4">Forgot Password?</h2>
          <p className="text-xl text-gray-600 font-light max-w-lg text-center px-6">
            Enter your account email address below and we will send you an email with password reset
            instructions
          </p>
        </div>

        <div className="container mx-auto px-6 pb-12">
          <div className="max-w-md mx-auto">
            <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-lg">
              <form onSubmit={formik.handleSubmit} className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className="text-gray-700 font-medium">
                    Email
                  </label>
                  <input
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:outline-none transition-colors"
                    id="email"
                    type="email"
                    onChange={formik.handleChange}
                    value={formik.values.email}
                  />
                  {formik.touched.email && formik.errors.email ? (
                    <p className="text-red-500 text-sm">{formik.errors.email}</p>
                  ) : null}
                </div>

                <div className="flex justify-center">
                  <Button isLoading={submitting} type="submit">
                    Send instructions
                  </Button>
                </div>
              </form>

              <NavLink
                to="/login"
                state={{ from: redirectFrom }}
                className="flex justify-center mt-6 text-cyan-500 hover:text-cyan-600 underline transition-colors"
              >
                Back to Log In
              </NavLink>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export const ResetPassword = () => {
  const { resetPasswordToken } = useParams();

  const [submitting, setSubmitting] = useState(false);
  const [passwordIsReset, setPasswordIsReset] = useState(false);

  const formik = useFormik({
    initialValues: {
      password: '',
      passwordConfirm: '',
    },
    validate: values => {
      const errors = {};
      if (!values.password) {
        errors.password = 'Required';
      }
      if (!values.passwordConfirm) {
        errors.passwordConfirm = 'Required';
      } else if (values.password !== values.passwordConfirm) {
        errors.passwordConfirm = 'Passwords do not match';
      }
      return errors;
    },
    onSubmit: async values => {
      setSubmitting(true);

      try {
        const rawResp = await fetch('/api/auth/reset-password', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            password: values.password,
            token: resetPasswordToken,
          }),
        });
        if (statusIsGood(rawResp.status)) {
          setPasswordIsReset(true);
          return;
        } else {
          throw new Error('Something went wrong. Please try again later.');
        }
      } catch (error) {
        toast.error(error.message);
        setSubmitting(false);
      }
    },
  });

  if (passwordIsReset) {
    // get the redirect path from local storage and pass it to the login page
    const redirectFrom = localStorage.getItem('passwordResetRedirect') || '/';
    return (
      <div className="flex flex-col min-h-screen">
        <div className="bg-gradient-to-br from-cyan-50 to-white pt-12">
          <div className="flex flex-col items-center mb-12">
            <h2 className="text-4xl md:text-5xl font-light text-black mb-4">Password Reset</h2>
          </div>

          <div className="container mx-auto px-6 pb-12">
            <div className="max-w-md mx-auto">
              <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-lg text-center">
                <p className="text-gray-700">
                  Your password has successfully been reset. Please{' '}
                  <NavLink
                    to="/login"
                    state={{ from: redirectFrom }}
                    className="text-cyan-500 hover:text-cyan-600 underline transition-colors"
                  >
                    log in
                  </NavLink>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main className="flex flex-col min-h-screen">
      <div className="bg-gradient-to-br from-cyan-50 to-white pt-12">
        <div className="flex flex-col items-center mb-12">
          <Lock className="w-16 h-16 text-cyan-500 mb-4" />
          <h2 className="text-4xl md:text-5xl font-light text-black mb-4">Reset Your Password</h2>
          <p className="text-xl text-gray-600 font-light max-w-lg text-center px-6">
            Enter your new password below to secure your account
          </p>
        </div>

        <div className="container mx-auto px-6 pb-12">
          <div className="max-w-md mx-auto">
            <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-lg">
              <form onSubmit={formik.handleSubmit} className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <label htmlFor="password" className="text-gray-700 font-medium">
                    New Password
                  </label>
                  <input
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:outline-none transition-colors"
                    id="password"
                    type="password"
                    onChange={formik.handleChange}
                    value={formik.values.password}
                  />
                  {formik.touched.password && formik.errors.password ? (
                    <p className="text-red-500 text-sm">{formik.errors.password}</p>
                  ) : null}
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="passwordConfirm" className="text-gray-700 font-medium">
                    Confirm Password
                  </label>
                  <input
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:outline-none transition-colors"
                    id="passwordConfirm"
                    type="password"
                    onChange={formik.handleChange}
                    value={formik.values.passwordConfirm}
                  />
                  {formik.touched.passwordConfirm && formik.errors.passwordConfirm ? (
                    <p className="text-red-500 text-sm">{formik.errors.passwordConfirm}</p>
                  ) : null}
                </div>

                <div className="flex justify-center">
                  <Button isLoading={submitting} type="submit">
                    Reset password
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};
